import {
  isClassicSource,
  isModuleSource
} from "@common-core/react-composable/script-injector";
import {IconType} from "../lib/interstate";
import {EmbeddedComponent} from "./EmbeddedComponent";
import {isSidebarComponent} from "./SidebarComponent";
import {isCommonOrgTabComponent} from "./CommonOrgTabComponent";

/**
 * Applies URL parameter overrides to an embedded component definition.
 * Supports EmbeddedComponent and its extensions (SidebarComponent, CommonOrgTabComponent).
 *
 * URL parameter format:
 * - `?${id}.script.id=SCRIPT_ID` - Override script id
 * - `?${id}.script.async=true|false` - Override script async
 * - `?${id}.script.defer=true|false` - Override script defer
 * - `?${id}.script.source.module=URL` - Override module source
 * - `?${id}.script.source.fallback=URL` - Override module fallback
 * - `?${id}.script.source.classic=URL` - Override classic source
 * - `?${id}.element=ELEMENT_NAME` - Override element property
 * - `?${id}.attributes.{key}=VALUE` - Override attribute key
 * - `?${id}.preload=true|false` - Override preload boolean
 * - `?${id}.path=PATH` - Override path (SidebarComponent only)
 * - `?${id}.icon=ICON` - Override icon (SidebarComponent only)
 * - `?${id}.hash=HASH` - Override hash (CommonOrgTabComponent only)
 * - `?${id}.contained=true|false` - Override contained boolean (CommonOrgTabComponent only)
 *
 * @example
 * ```typescript
 * const component: EmbeddedComponent = {
 *   id: "my-component",
 *   element: "my-component-root-element",
 *   script: { id: "my-component-module", source: { module: "https://prod.com/component.js" } }
 * };
 *
 * // URL: ?my-component.script.source.module=https://localhost:3000/component.js
 * const override = buildEmbeddedComponentOverride(params, component);
 * ```
 */
export const buildEmbeddedComponentOverride = (
  params: URLSearchParams,
  component: EmbeddedComponent
): EmbeddedComponent => {
  const override: EmbeddedComponent = {
    ...component,
    script: {
      ...component.script,
      source: {...component.script.source}
    }
  };

  // script.id override
  const scriptId = params.get(`${component.id}.script.id`);
  if (scriptId) {
    override.script.id = scriptId;
  }

  // script.async override
  const scriptAsync = params.get(`${component.id}.script.async`);
  if (scriptAsync !== null) {
    override.script.async = scriptAsync === "true";
  }

  // script.defer override
  const scriptDefer = params.get(`${component.id}.script.defer`);
  if (scriptDefer !== null) {
    override.script.defer = scriptDefer === "true";
  }

  // root element override
  const element = params.get(`${component.id}.element`);
  if (element) {
    override.element = element;
  }

  // script source (module) override
  if (isModuleSource(override.script.source)) {
    const module = params.get(`${component.id}.script.source.module`);
    if (module) {
      override.script.source.module = module;
    }

    const fallback = params.get(`${component.id}.script.source.fallback`);
    if (fallback) {
      override.script.source.fallback = fallback;
    }
  }

  // script source (classic) override
  if (isClassicSource(override.script.source)) {
    const classic = params.get(`${component.id}.script.source.classic`);
    if (classic) {
      override.script.source = {classic};
    }
  }

  // attributes override
  for (const [key, value] of params.entries()) {
    const prefix = `${component.id}.attributes.`;
    if (key.startsWith(prefix)) {
      const attributeName = key.substring(prefix.length);
      if (!override.attributes) {
        override.attributes = {};
      }
      override.attributes[attributeName] = value;
    }
  }

  // preload override
  const preload = params.get(`${component.id}.preload`);
  if (preload !== null) {
    override.preload = preload === "true";
  }

  // SidebarComponent-specific overrides
  if (isSidebarComponent(override)) {
    const path = params.get(`${component.id}.path`);
    if (path) {
      override.path = path;
    }

    const icon = params.get(`${component.id}.icon`);
    if (icon) {
      override.icon = icon as unknown as IconType;
    }
  }

  // CommonOrgTabComponent-specific overrides
  if (isCommonOrgTabComponent(override)) {
    const hash = params.get(`${component.id}.hash`);
    if (hash) {
      override.hash = hash;
    }

    const contained = params.get(`${component.id}.contained`);
    if (contained !== null) {
      override.contained = contained === "true";
    }
  }

  console.groupCollapsed(`COAT EmbeddedComponent Definition - ${component.id}`);
  console.dir(override);
  console.groupEnd();

  return override;
};
